import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { forkJoin, lastValueFrom } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';

import { USERDETAILS_MAX_PARAM_NUMBER } from '@plugins/middleware-lib';
import {
  returnAsArray,
  splitArrayByMaxLength,
} from '@plugins/shared/utils/arrayUtils/array-utils';
import { IdentityPublic } from '@plugins/userdetails-lib/entity';

import { API_URL } from '../../private/config';
import {
  ApiRequestTypes,
  ApiEntityTypes,
  PendingRequest,
  AllowedQueries,
  ApiDetailsRequestDto,
} from '../../private/utils/types.private';
import { setParams } from '../../private/utils/utils';

import { EntitiesCacheManagementService } from './entities-cache-management.service';

/** **Responsible for:**
* - Sending requests for data to the server.
* - Request de-duplication.
* - Updating the entity caches with the response objects.
*
* The service's methods and observables are called / subscribed to by the `UserDetailsService`.

* **Note**:
* - Deduplication is only relevant for entities that have not yet been cached.
* Cached entities are served directly so that no further requests for those ids
* will be issued to the service from the `UserDetailsService`. That is why the latter will also
* call the `prunePendingRequest` method, if all requested ids are already found in the cache.
- In the case of searching for entities via search terms, request deduplication is not used, as this is already performed via the searchCache. `UserDetailsService` will directly call the `directRequest` method for the nonCached ids. */
@Injectable({ providedIn: 'root' })
export class EntitiesRequestsService {
  protected readonly paramLimit: number = USERDETAILS_MAX_PARAM_NUMBER;
  protected readonly http = inject(HttpClient);
  protected readonly cacheService = inject(EntitiesCacheManagementService);

  #pendingRequests: PendingRequest[] = [];

  get pendingRequests(): PendingRequest[] {
    return this.#pendingRequests;
  }
  set pendingRequests(requests: PendingRequest[]) {
    this.#pendingRequests = requests;
  }

  /** Request data without any caching */
  directRequest<T extends ApiRequestTypes>(
    searchType: T,
    query: AllowedQueries<T>,
    groupType: ApiEntityTypes = 'all',
  ) {
    const params = setParams({ query, groupType });
    const targetUrl = `${API_URL.base}${API_URL[searchType]}${groupType}`;
    return this.http.get<IdentityPublic[]>(targetUrl, { params });
  }

  /** Fire new requests or return pending ones, if ids and types match.
   *
   * Currently,  only `details` request types are subject to deduplication. */
  async handleRequest(
    _requestType: 'details',
    query: ApiDetailsRequestDto,
    type: ApiEntityTypes,
  ): Promise<IdentityPublic[] | undefined> {
    const ids = returnAsArray(query.id);
    const incomingRequestCheck = this.checkIncomingRequests(ids, type);
    if (incomingRequestCheck) {
      return await lastValueFrom(incomingRequestCheck.request);
    }

    // Create a new pending request and find related requests.
    const { nonQueriedIds, relatedRequests } = this.checkPresenceOfIds(
      ids,
      type,
    );
    const relatedIndexes = [...relatedRequests];
    if (nonQueriedIds.length) {
      this.pushPendingDetailRequests(nonQueriedIds, type, relatedIndexes);
    }
    // Await the new pending request AND all related pending requests.
    if (relatedIndexes.length) {
      const entityData = await lastValueFrom(
        forkJoin(
          relatedIndexes.map((index) => this.pendingRequests[index].request),
        ),
      );
      return entityData.flat();
    }
    return undefined;
  }

  private checkIncomingRequests(
    ids: string[],
    type: ApiEntityTypes,
  ): PendingRequest | undefined {
    return this.pendingRequests.find(
      ({ ids: pendingIds, targetGroup }) =>
        targetGroup === type && ids.every((id) => pendingIds.includes(id)),
    );
  }

  private checkPresenceOfIds(
    ids: string[],
    type: ApiEntityTypes,
  ): {
    relatedRequests: number[];
    nonQueriedIds: string[];
  } {
    const presentIds = this.pendingRequests.map((entry) => {
      return entry.targetGroup === type ? entry.ids : false;
    });
    const relatedRequests = presentIds
      .map((pendingIds, index) => {
        if (pendingIds && ids.some((id) => pendingIds.includes(id))) {
          return index;
        }
        return undefined;
      })
      .filter((entry): entry is number => entry !== undefined);
    return {
      relatedRequests,
      nonQueriedIds: ids.filter((id) => !presentIds.flat().includes(id)),
    };
  }

  /** If all ids of a pendingRequest are included within
   *  the passed ids, remove the pending request. */
  private prunePendingRequest(ids: string[], type: ApiEntityTypes): void {
    const targetRequests = this.pendingRequests.filter(
      ({ targetGroup }) => type === targetGroup,
    );
    if (!targetRequests.length) return;

    this.pendingRequests = targetRequests.filter(
      (requestStack) => !requestStack.ids.every((id) => ids.includes(id)),
    );
  }

  /** Generate target requests and split them into multiple parts,
   * if query parameters are over limit. */
  private pushPendingDetailRequests(
    ids: string[],
    type: ApiEntityTypes,
    relatedIndexes: number[],
  ) {
    const idParts = splitArrayByMaxLength(ids, this.paramLimit);
    idParts.forEach((ids) => {
      this.pendingRequests.push({
        ids: ids,
        requestType: 'details',
        targetGroup: type,
        request: this.directRequest('details', { id: ids }, type).pipe(
          tap((entityResponse) => {
            this.cacheService.updateEntityCacheAndReturn(ids, entityResponse);
          }),
          shareReplay(1),
          finalize(() => {
            this.prunePendingRequest(ids, type);
          }),
        ),
      });
      relatedIndexes.push(this.pendingRequests.length - 1);
    });
  }
}
